import React, { CSSProperties } from 'react';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { DeliveryWindow } from '@bringg/types';
import SelectableList from '../../components/selectable-list/selectable-list';
import DeliveryWindowCalendar from '../../components/delivery-window-calendar/delivery-window-calendar';
import { ScreenType } from '../../types/common.consts';

interface Props {
	deliveryWindows: DeliveryWindow[];
	onDeliveryWindowSelect: (deliveryWindow: DeliveryWindow) => void;
	className?: string;
	style?: CSSProperties;
	deliveryWindowClassName?: string;
	deliveryWindowStyle?: CSSProperties;
	locale?: string;
	timezone?: string;
	showCalendar?: boolean;
	screenType?: ScreenType;
	/** A string representation for a date */
	initialDate?: string;
	fullscreen?: boolean;
	schedulingCustomerMessage?: JSX.Element;
	externalIdInfo?: string;
}

export const LOCALIZED_DATE_FORMAT = 'LLLL';
export const LOCALIZED_TIME_FORMAT = 'LT';

export const formatDateWithoutYear = (date: string, locale: string, timezone: string) => {
	const year = moment(date).format('YYYY');
	const localizedFormattedDate = formatDate(date, LOCALIZED_DATE_FORMAT, timezone, locale);

	// localized format without year
	return localizedFormattedDate.replace(` ${year}`, '');
};
const formatDate = (date: string, format: string, timezone?: string, locale?: string): string => {
	let formatDate = moment(date);

	if (timezone) {
		formatDate = formatDate.tz(timezone);
	}
	if (locale) {
		formatDate = formatDate.locale(locale);
	}

	return formatDate.format(format);
};
const normalizeDeliveryWindow = ({ start_time, end_time }: DeliveryWindow, locale?: string, timezone?: string) => {
	try {
		if (locale) {
			require(`dayjs/locale/${locale}.js`);
			// According to dayjs docs locale become active when it is loaded
			// But during testing this appeared to be not true or flaky
			dayjs.locale(locale);
			require(`moment/dist/locale/${locale}`);
		}
	} catch (e) {
		console.warn(`Failed loading locale: ${locale}`);
	}
	return `${formatDateWithoutYear(start_time, locale, timezone)} - ${formatDate(
		end_time,
		LOCALIZED_TIME_FORMAT,
		timezone,
		locale
	)}`;
};

const DeliveryWindows: React.FC<Props> = ({
	deliveryWindows,
	onDeliveryWindowSelect,
	className,
	deliveryWindowClassName,
	style,
	deliveryWindowStyle,
	showCalendar,
	initialDate,
	locale = 'en',
	timezone = moment.tz.guess() || 'UTC',
	screenType = ScreenType.DESKTOP,
	fullscreen = true,
	schedulingCustomerMessage,
	externalIdInfo
}) => {
	return (
		<>
			{showCalendar ? (
				<DeliveryWindowCalendar
					deliveryWindows={deliveryWindows}
					onDeliveryWindowSelected={onDeliveryWindowSelect}
					screenType={screenType}
					locale={locale}
					timezone={timezone}
					className={className}
					initialDate={initialDate}
					fullscreen={fullscreen}
					schedulingCustomerMessage={schedulingCustomerMessage}
					externalIdInfo={externalIdInfo}
				/>
			) : (
				<SelectableList
					className={classNames('delivery-windows', className)}
					itemClassName={classNames('delivery-window', deliveryWindowClassName)}
					style={style}
					itemStyle={deliveryWindowStyle}
					items={deliveryWindows}
					onSelect={onDeliveryWindowSelect}
					renderValue={deliveryWindow => normalizeDeliveryWindow(deliveryWindow, locale, timezone)}
				/>
			)}
		</>
	);
};

export default DeliveryWindows;
